import React, { useEffect, useMemo } from 'react';

import { TagProps } from './tag.types';
import { CaretDownIcon, Popover } from '../..';
import { addStyles } from '../../utils/helpers';

const options = {
  default: {
    base: 'inline-block border rounded-sm px-2 text-xs font-medium uppercase text-gray-900',
    text: 'align-middle my-1 inline-block',
    type: {
      default: 'bg-gray-50 border-gray-500',
      error: 'bg-red-50 border-red-500 text-red-600',
      processing: 'bg-blue-50 border-blue-300 text-blue-800',
      warning: 'bg-gold-50 border-gold-800',
      success: 'bg-green-50 border-green-700 text-green-800',
    },
    caretIcon: 'h-4 w-4 inline fill-current cursor-pointer',
    rightIcon: 'size-5.5 inline cursor-pointer',
    leftIcon: 'h-6 w-6 inline',
  },
  table: {
    base: 'table-tag mr-1 normal-case border-0 inline-block border rounded px-2 text-xs font-medium text-gray-900',
    text: 'align-middle inline-block',
    type: {
      default: 'bg-gray-100 border-gray-500',
      error: 'bg-red-50 border-red-500 text-red-600',
      processing: 'bg-blue-50 border-blue-300 text-blue-800',
      warning: 'bg-gold-50 border-gold-800',
      success: 'bg-green-50 border-green-700 text-green-800',
    },
    caretIcon: 'h-4 w-4 inline fill-current cursor-pointer',
    rightIcon: 'size-5.5 inline cursor-pointer',
    leftIcon: 'h-6 w-6 inline',
  },
};

// eslint-disable-next-line max-lines-per-function
export function Tag({
  baseType = 'default',
  menuContent,
  type = 'default',
  text,
  leftIcon,
  leftIconClassName,
  rightIcon,
  className,
  isMenuOpen = false,
  onRightClick,
  onClick,
  onMenuOpenChange,
}: TagProps) {
  const [menuOpen, setMenuOpen] = React.useState(isMenuOpen);
  const icon = menuContent
    ? CaretDownIcon({ title: 'caret', className: options[baseType].caretIcon, onClick: onRightClick })
    : rightIcon?.({ className: options[baseType].rightIcon, onClick: onRightClick });

  useEffect(() => {
    setMenuOpen(isMenuOpen);
  }, [isMenuOpen]);

  const containerClasses = useMemo(() => {
    const base = options[baseType].base;
    return addStyles([className ?? base, options[baseType].type[type], menuContent ? 'cursor-pointer' : '']);
  }, [baseType, className, type, menuContent]);

  return (
    <Popover
      open={menuOpen}
      trigger="external"
      innerContentClassName=""
      showArrow={false}
      content={menuContent}
      onOpenChange={(isOpen: boolean) => {
        setMenuOpen(isOpen);
        onMenuOpenChange?.(isOpen);
      }}
    >
      <div
        className={containerClasses}
        onClick={(e) => {
          if (menuContent) {
            setMenuOpen(true);
            onMenuOpenChange?.(true);
          }
          onClick?.(e);
        }}
      >
        <span>{leftIcon?.({ className: addStyles([leftIconClassName ?? options[baseType].leftIcon]) })}</span>
        <div className={options[baseType].text}>{text}</div>
        <div className="inline overflow-hidden">{icon}</div>
      </div>
    </Popover>
  );
}

export default Tag;
